import {Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, ViewChild, ChangeDetectorRef} from '@angular/core';
import {DataService} from '../../../Services/data.service';
import {NotificationService} from '../../../Services/notification.service';
import {HttpErrorResponse} from '@angular/common/http';
import {EmailModel} from '../../../DataModels/email-model';
import {BsToastService} from '../../../Services/bs-toast-service';
import {firstValueFrom} from 'rxjs';
import {Modal} from 'bootstrap';
import {StorageService} from '../../../Services/storage.service';
import {AppConfigService} from '../../../Services/app-config.service';
import {EmailService} from '../../../Services/email.service';
import {SendEmailModalComponent} from '../send-email-modal/send-email-modal.component';

@Component({
  selector: 'app-audit-trail-modal',
  templateUrl: './audit-trail-modal.component.html',
  styleUrls: ['./audit-trail-modal.component.css'],
  standalone: false
})
export class AuditTrailModalComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('closeButton') closeButton: ElementRef;
  @ViewChild('EmailContent', {static: false}) EmailContent: ElementRef;
  // Reference the embedded Send Email modal component's host element
  @ViewChild(SendEmailModalComponent, {read: ElementRef}) private emailModalHost: ElementRef;

  searchText: string;

  @Input() src: string;
  @Input() docNumber: any;
  @Input() modalId: string = 'auditTrailModal';

  auditTrailList: Array<object>;
  auditTrailCount = 0;

  // Promise used to coordinate async loading with email/print generation
  private auditLoadPromise: Promise<any[]> | null = null;

  showAudit = false;

  // Email modal binding object for this modal
  emailObject: EmailModel = new EmailModel();

  constructor(private table: DataService,
              private toast: NotificationService,
              private notification: BsToastService,
              private cdr: ChangeDetectorRef,
              private storage: StorageService,
              private appConfig: AppConfigService,
              private emailSvc: EmailService) {
  }

  ngOnInit() {
    // this.loadAuditTrail();
  }

  ngOnChanges() {
    // if it is called before there is a source do nothing.
    if (!this.src) {
      return;
    }
    this.loadAuditTrail();
  }

  loadAuditTrail() {

    // if (this.src === '') {
    //  // console.log('no rec: ' + this.src);
    //   this.table.getData('audit_trail').subscribe(
    //     result => {
    //       this.auditTrailList = result;
    //       this.auditTrailCount = this.auditTrailList.length;
    //       // console.log('empty src:' + this.src);
    //     },
    //     (error: HttpErrorResponse) => {
    //       console.log(error);
    //       // this.toast.error($localize`:Audit Trail Error @@auditTrailError:Error loading Audit Trail Data`);
    //     },
    //     () => {
    //     }
    //   );
    // }

    if (this.src !== '') {
      // Create a memoized promise representing the data load so callers can await it
      this.auditLoadPromise = new Promise<any[]>((resolve, reject) => {
        this.table
          .getDataWithParams('audit_trail/recid', 'recId=' + this.src)
          .pipe()
          .subscribe({
            next: (result) => {
              // Normalize result to array shape: either raw array or ResponseModel with Data array
              const arr = Array.isArray(result)
                ? (result as any[])
                : (Array.isArray((result as any)?.Data) ? (result as any).Data : []);
              this.auditTrailList = arr;
              this.auditTrailCount = arr.length;
              resolve(arr as any[]);
            },
            error: (error: HttpErrorResponse) => {
              console.log(error);
              // optionally: this.toast.showErrorToast('Error loading Audit Trail Data');
              reject(error);
            },
            complete: () => {
              // no-op
            }
          });
      });
    }
  }

  private async ensureAuditDataReady(): Promise<void> {
    // If list already exists (even empty), no need to wait further
    if (this.auditTrailList !== undefined) {
      return;
    }

    // If there's an in-flight load, await it
    if (this.auditLoadPromise) {
      try {
        await this.auditLoadPromise;
      } catch {
        // swallow to allow graceful fallback
      }
      return;
    }

    // As a fallback, if we have a src, fetch once via firstValueFrom
    if (this.src) {
      try {
        const result = await firstValueFrom(
          this.table.getDataWithParams('audit_trail/recid', 'recId=' + this.src)
        );
        const arr = Array.isArray(result)
          ? (result as any[])
          : (Array.isArray((result as any)?.Data) ? (result as any).Data : []);
        this.auditTrailList = arr;
        this.auditTrailCount = arr.length;
      } catch (e) {
        console.error('Failed to load audit trail before building email/print HTML', e);
      }
    }
  }

  onPrint() {
    try {
      const html = this.buildPrintHtml();
      this.printHtmlInIframe(html);
    } catch (e) {
      console.error('Audit Trail print failed', e);
      this.notification.showErrorToast('Unable to print Audit Trail');
    }
  }

  async onEmail() {
    try {
      // Ensure audit trail data is fully loaded before generating HTML for email attachment
      await this.ensureAuditDataReady();

      // Capture the already-rendered HTML for the email attachment
      const html = this.buildPrintHtml(true);

      // Build EmailModel for Send Email modal (follow known-good pattern)
      const emailDocObj = {} as EmailModel;
      const emailDocumentName = 'Audit Trail';

      emailDocObj.print_orientation = '1';
      emailDocObj.convert_attachment_to = 'pdf';
      emailDocObj.fromEmail = '';
      emailDocObj.fromName = 'AcctXErp';
      emailDocObj.header_id = null as any;
      emailDocObj.toEmail = '';
      emailDocObj.body = 'Please see the attached ' + emailDocumentName + '.<br><br><br>'
        + 'Best regards,'
        + '<br><br>'
        + 'AcctXErp'
        + '<br><br>';
      emailDocObj.Subject = 'AcctXErp ' + emailDocumentName;
      emailDocObj.activity_message = emailDocumentName;

      try {
        emailDocObj.pdfHtml = html || '';
      } catch {
        emailDocObj.pdfHtml = '';
      }
      // console.log('html: ' + html);

      // Provide a deterministic file name for the attachment if generated
      const safeName = (emailDocumentName || 'document').replace(/\s+/g, '_');
      if (this.src != null && this.src !== '') {
        emailDocObj.FileName = `${safeName}_${this.src}.pdf`;
      } else {
        emailDocObj.FileName = `${safeName}.pdf`;
      }

      // Bind to Send Email modal input
      this.emailObject = emailDocObj;

      // Flush bindings so the modal sees updated input
      this.cdr.detectChanges();

      // Prefer to locate the Send Email modal element via the directive (component instance)
      let emailEl: HTMLElement | null = null;
      try {
        emailEl = (this.emailModalHost?.nativeElement as HTMLElement)?.querySelector?.('#sendEmailModal') || null;
      } catch {
        emailEl = null;
      }
      // Fallback to global lookup if not found via directive
      if (!emailEl) {
        emailEl = document.getElementById('sendEmailModal');
      }

      // If the modal element is rendered under the Audit Trail modal, move it to body so it overlays properly
      if (emailEl && emailEl.closest('#auditTrailModal')) {
        document.body.appendChild(emailEl);
      }

      // Open the Send Email modal programmatically (no data-bs-toggle)
      const modal = emailEl ? Modal.getOrCreateInstance(emailEl) : null;
      modal?.show();
    } catch (e) {
      console.error('Audit Trail prepare email failed', e);
      this.notification.showErrorToast('Unable to prepare email for Audit Trail');
    }
  }

  private printHtmlInIframe(html: string): void {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.setAttribute('aria-hidden', 'true');

    document.body.appendChild(iframe);

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) {
      this.notification.showErrorToast('Unable to access print frame');
      document.body.removeChild(iframe);
      return;
    }

    doc.open();
    doc.write(html);
    doc.close();

    const printAndCleanup = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } finally {
        setTimeout(() => {
          if (iframe && iframe.parentNode) {
            iframe.parentNode.removeChild(iframe);
          }
        }, 1000);
      }
    };

    if (doc.readyState === 'complete') {
      setTimeout(printAndCleanup, 250);
    } else {
      iframe.onload = () => setTimeout(printAndCleanup, 250);
    }
  }

  private buildPrintHtml(forEmail: boolean = false): string {
    const list: any[] = Array.isArray(this.auditTrailList) ? (this.auditTrailList as any[]) : [];
    const rows = list.map((at: any) => {
      const field = at?.column_name || '';
      const orig = at?.original_value ?? '';
      const newv = at?.new_value ?? '';
      const dt = at?.date_time ? String(at.date_time) : '';
      const user = at?.user_name || '';
      return `<tr><td>${this.escapeHtml(field)}</td><td>${this.escapeHtml(String(orig))}</td><td>${this.escapeHtml(String(newv))}</td><td>${this.escapeHtml(dt)}</td><td>${this.escapeHtml(user)}</td></tr>`;
    }).join('');

    const styles = `
      <style>
        @page { size: landscape; margin: 12mm; }
        @media print { .no-print { display: none !important; } }
        body { font-family: Arial, Helvetica, sans-serif; margin: 24px; }
        h1 { font-size: 18px; margin: 0 0 8px 0; }
        .meta { font-size: 12px; color: #555; margin-bottom: 8px; }
        .meta span { display: inline-block; margin-right: 16px; }
        table { width: 100%; border-collapse: collapse; margin-top: 12px; }
        th, td { border: 1px solid #ccc; padding: 6px 8px; font-size: 12px; }
        th { background: #f6f6f6; text-align: left; }
      </style>
    `;

    const title = `Audit Trail`;
    const runAt = new Date().toLocaleString();
    // Prefer a transaction number when available; fallback to src/id
    const transactionNumber = (this as any)?.docNumber ?? (((this as any)?.vm?.item?.header?.number) || this.src || '');

    return `
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${this.escapeHtml(title)}</title>
          ${styles}
        </head>
        <body>
          <h1>${this.escapeHtml(title)}</h1>
          <div class="meta"><span><strong>Run:</strong> ${this.escapeHtml(runAt)}</span><span><strong>Transaction #:</strong> ${this.escapeHtml(String(transactionNumber))}</span><span><strong>Changed Fields:</strong> ${this.auditTrailCount || 0}</span></div>
          <table>
            <thead>
              <tr><th>Field</th><th>Original Value</th><th>New Value</th><th>Date Time</th><th>User Name</th></tr>
            </thead>
            <tbody>
              ${rows}
            </tbody>
          </table>
          <div class="no-print" style="margin-top:16px"><button onclick="window.print()">Print</button></div>
        </body>
      </html>
    `;
  }

  private escapeHtml(text: string): string {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  toDate(value: string): Date | null {
    if (!value) {
      return null;
    }
    return new Date(value);
  }

  toggleAudit() {
    this.showAudit = !this.showAudit;
  }

  ngOnDestroy() {
    const modalEl = this.closeButton?.nativeElement?.closest('.modal');
    if (modalEl) {
      const modal = Modal.getInstance(modalEl);
      if (modal) {
        modal.hide();
      }
    }
  }

}
